import { useCallback, useMemo } from 'react';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { KaraokeLine } from '@src/editor/types/karaoke';

export interface UseKaraokeResult {
  // Query methods
  getKaraokeConfigs: () => Map<string, KaraokeConfig> | null;
  getKaraokeLines: () => KaraokeLine[];
  getDefaultKaraokeConfig: () => KaraokeConfig | null;
  getKaraokeConfigForKaraokeClipId: (
    karaokeClipId: string,
  ) => KaraokeConfig | null;

  // Config management handlers
  setDefaultKaraokeConfigId: (configId: string) => void;
  createKaraokeConfig: (config: Omit<KaraokeConfig, 'id'>) => KaraokeConfig;
  updateKaraokeConfigAndProduce: (config: KaraokeConfig) => void;

  commandDeleteKaraoke: () => boolean;

  // Command handlers from IKaraokeCommands
  commandProduceKaraoke: (options?: { replaceTrack: boolean }) => boolean;
  commandApplyKaraokeConfig: (
    config: { id: string } | Omit<KaraokeConfig, 'id'>,
    karaokeClipIds?: string[],
  ) => boolean;
  commandAddBreak: (transcriptTokenId: string) => boolean;
  commandRemoveBreak: (transcriptTokenId: string) => boolean;
  commandAdjustKaraokeClipTime: (
    karaokeClipId: string,
    offset: { startSeconds?: number; endSeconds?: number },
  ) => boolean;
  commandHideInKaraoke: (transcriptTokenIds: string[]) => boolean;
  commandShowInKaraoke: (transcriptTokenIds: string[]) => boolean;
}

/**
 * React hook for managing karaoke operations in the video editor
 * Provides action handlers to call karaoke commands and manage karaoke configuration
 *
 * @param editor - IVideoEditor instance
 * @returns Object with karaoke operation handlers
 */
export function useKaraoke(editor: IVideoEditor | null): UseKaraokeResult {
  // Query methods
  const getKaraokeConfigs = useCallback(() => {
    if (!editor) return null;
    return editor.getKaraokeConfigs();
  }, [editor]);

  const getKaraokeLines = useCallback(() => {
    if (!editor) return [];
    return editor.getKaraokeLines();
  }, [editor]);

  const getDefaultKaraokeConfig = useCallback(() => {
    if (!editor) return null;
    return editor.getDefaultKaraokeConfig();
  }, [editor]);

  const getKaraokeConfigForKaraokeClipId = useCallback(
    (karaokeClipId: string): KaraokeConfig | null => {
      if (!editor) return null;
      const karaokeClip =
        editor
          .getKaraokeTrack()
          ?.karaokeClips.find(element => element.id === karaokeClipId) || null;
      if (!karaokeClip) return null;
      return (
        editor.getKaraokeConfigs().get(karaokeClip.karaokeConfig.id) || null
      );
    },
    [editor],
  );

  // Config management handlers
  const setDefaultKaraokeConfigId = useCallback(
    (configId: string) => {
      if (!editor) return;
      editor.setDefaultKaraokeConfigId(configId);
    },
    [editor],
  );

  const createKaraokeConfig = useCallback(
    (config: Omit<KaraokeConfig, 'id'>): KaraokeConfig => {
      if (!editor) {
        throw new Error('Editor instance is not available');
      }
      return editor.createKaraokeConfig(config);
    },
    [editor],
  );

  const updateKaraokeConfigAndProduce = useCallback(
    (config: KaraokeConfig) => {
      if (!editor) return;
      const existingConfig = editor.getKaraokeConfigs().get(config.id);
      if (!existingConfig) return;
      return editor.commandUpdateKaraokeConfig(config);
    },
    [editor],
  );

  const commandDeleteKaraoke = useCallback(() => {
    if (!editor) return false;
    const karaokeTrackId = editor.getKaraokeTrack()?.id;
    if (!karaokeTrackId) return false;
    return !!editor.commandRemoveTrack(karaokeTrackId);
  }, [editor]);

  // Command handlers from IKaraokeCommands
  const commandProduceKaraoke = useCallback(
    (options?: { replaceTrack: boolean }) => {
      if (!editor) return false;
      return editor.commandProduceKaraoke(options);
    },
    [editor],
  );

  const commandApplyKaraokeConfig = useCallback(
    (
      config: { id: string } | Omit<KaraokeConfig, 'id'>,
      karaokeClipIds?: string[],
    ) => {
      if (!editor) return false;
      return editor.commandApplyKaraokeConfig(config, karaokeClipIds);
    },
    [editor],
  );

  const commandAddBreak = useCallback(
    (transcriptTokenId: string) => {
      if (!editor) return false;
      return editor.commandAddBreak(transcriptTokenId);
    },
    [editor],
  );

  const commandRemoveBreak = useCallback(
    (transcriptTokenId: string) => {
      if (!editor) return false;
      return editor.commandRemoveBreak(transcriptTokenId);
    },
    [editor],
  );

  const commandAdjustKaraokeClipTime = useCallback(
    (
      karaokeClipId: string,
      offset: { startSeconds?: number; endSeconds?: number },
    ) => {
      if (!editor) return false;
      return editor.commandAdjustKaraokeClipTime(karaokeClipId, offset);
    },
    [editor],
  );

  const commandHideInKaraoke = useCallback(
    (transcriptTokenIds: string[]) => {
      if (!editor) return false;
      return editor.commandHideInKaraoke(transcriptTokenIds);
    },
    [editor],
  );

  const commandShowInKaraoke = useCallback(
    (transcriptTokenIds: string[]) => {
      if (!editor) return false;
      return editor.commandShowInKaraoke(transcriptTokenIds);
    },
    [editor],
  );

  return useMemo(
    () => ({
      // Query methods
      getKaraokeConfigs,
      getKaraokeLines,
      getDefaultKaraokeConfig,
      getKaraokeConfigForKaraokeClipId,

      // Config management handlers
      setDefaultKaraokeConfigId,
      createKaraokeConfig,
      updateKaraokeConfigAndProduce,

      // Command handlers
      commandProduceKaraoke,
      commandDeleteKaraoke,
      commandApplyKaraokeConfig,
      commandAddBreak,
      commandRemoveBreak,
      commandAdjustKaraokeClipTime,
      commandHideInKaraoke,
      commandShowInKaraoke,
    }),
    [
      getKaraokeConfigs,
      getKaraokeLines,
      getDefaultKaraokeConfig,
      setDefaultKaraokeConfigId,
      createKaraokeConfig,
      commandProduceKaraoke,
      commandApplyKaraokeConfig,
      commandAddBreak,
      commandRemoveBreak,
      commandAdjustKaraokeClipTime,
      commandHideInKaraoke,
      commandShowInKaraoke,
    ],
  );
}
